import { ATHENA_STT_EVENTS, SpeechToTextProvider } from '../SpeechToTextProvider';
import {
  addNativeVoiceListener,
  getNativeVoicePlugin,
  isNativeVoicePluginAvailable,
  getNativeMicrophonePermission,
  requestNativeMicrophonePermission,
} from '../NativeVoicePlugin';
import { recordVoiceDiagnostic } from '../voiceDiagnostics';

const STOP_FALLBACK_MS = 2200;

/**
 * Adapter compatible with WebSpeechAdapter. The VoiceEngineV2 remains owner
 * of the turn buffer and endpointing; Android only owns microphone/STT
 * lifecycle and emits normalized segments.
 */
export class NativeSpeechAdapter extends SpeechToTextProvider {
  constructor(options = {}) {
    super();
    this.lang = options.lang || 'pt-BR';
    this.continuous = Boolean(options.continuous);
    this.interimResults = options.interimResults !== false;
    this.sessionId = options.sessionId || `native-stt-${Date.now()}`;
    // A logical manual turn can span multiple short Android recognizer
    // sessions. Unlike the transient `sessionId`, this id is stable until the
    // user presses send and lets the native layer deduplicate interaction
    // cues even when a bridge callback races a transparent retry.
    this.manualTurnId = typeof options.manualTurnId === 'string' && options.manualTurnId
      ? options.manualTurnId
      : this.sessionId;
    this._playManualStartCue = Boolean(options.playStartCue);
    this.onResult = null;
    this.onEnd = null;
    this.onError = null;
    this.onEvent = null;
    this.onPermissionState = typeof options.onPermissionState === 'function' ? options.onPermissionState : null;
    this._active = false;
    this._ended = false;
    this._stopping = false;
    // `startListening()` atravessa diversas promises do bridge. Estes flags
    // impedem que uma resolução tardia reabra o microfone depois do segundo
    // toque do usuário.
    this._nativeStarted = false;
    // Alguns serviços Android reutilizam resultIndex=0 a cada frase. O id de
    // segmento mantém cada frase dentro do mesmo turno manual.
    this._segmentCounter = 0;
    this._pendingSegmentId = null;
    this._cleanup = [];
    this._stopFallback = null;
  }

  getCapabilities() {
    return {
      microphone: isNativeVoicePluginAvailable(),
      stt: isNativeVoicePluginAvailable(),
      continuous: isNativeVoicePluginAvailable(),
    };
  }

  start() {
    if (!isNativeVoicePluginAvailable()) return false;
    this._active = true;
    void this._connectAndStart();
    return true;
  }

  async _connectAndStart() {
    const plugin = getNativeVoicePlugin();
    if (!plugin || !this._active) return;

    const removeState = await addNativeVoiceListener('recognitionState', (detail) => this._onRecognitionState(detail));
    const removePartial = await addNativeVoiceListener('partialResult', (detail) => this._onPartial(detail));
    const removeFinal = await addNativeVoiceListener('finalResult', (detail) => this._onFinal(detail));
    const removeError = await addNativeVoiceListener('recognitionError', (detail) => this._onError(detail));
    const removeLevel = await addNativeVoiceListener('recognitionLevel', (detail) => this._onRecognitionLevel(detail));
    this._cleanup.push(removeState, removePartial, removeFinal, removeError, removeLevel);

    if (!this._active || this._stopping) {
      this._removeListeners();
      return;
    }

    try {
      let permission = await getNativeMicrophonePermission();
      if (!this._active || this._stopping) return;

      if (!permission.granted) {
        this.onPermissionState?.({ state: 'requesting', permission: permission.state });
        permission = await requestNativeMicrophonePermission();
        if (!this._active || this._stopping) return;
      }

      if (!permission.granted) {
        this.onPermissionState?.({ state: 'denied', permission: permission.state, error: permission.error });
        this._fail(permission.error || 'not-allowed');
        return;
      }

      this.onPermissionState?.({ state: 'granted', permission: permission.state });
      const result = await plugin.startListening({
        sessionId: this.sessionId,
        language: this.lang,
        mode: this.continuous ? 'continuous' : 'manual',
        manualTurnId: this.manualTurnId,
        // Native cue playback is deliberately opt-in. A manual turn may get
        // multiple Android recognizer retries, but only its first real start
        // is allowed to make the recording cue.
        playStartCue: this._playManualStartCue,
        partialResults: this.interimResults,
        preferOnDevice: true,
      });

      // O segundo toque pode ocorrer enquanto a promise do Capacitor ainda
      // está voltando. Cancele uma sessão que tenha iniciado tardiamente em
      // vez de reabrir o microfone após o envio.
      if (!this._active || this._stopping) {
        if (result?.started) void plugin.cancelListening?.({ sessionId: this.sessionId });
        return;
      }
      if (result?.started === false) {
        const reason = result?.permission === 'denied'
          ? 'not-allowed'
          : (result?.reason || 'recognition_unavailable');
        this._fail(reason);
        return;
      }
      this._nativeStarted = true;
    } catch (error) {
      if (!this._active || this._stopping) return;
      this.onPermissionState?.({ state: 'error', error: error?.message || 'recognition_start_failed' });
      this._fail(error?.message || 'recognition_start_failed');
    }
  }

  _matches(detail) {
    return this._active && detail?.sessionId === this.sessionId;
  }

  _onRecognitionState(detail) {
    if (!this._matches(detail)) return;
    const state = detail?.state;
    if (state === 'listening') {
      recordVoiceDiagnostic(ATHENA_STT_EVENTS.LISTENING_START, { provider: detail?.provider, sessionId: this.sessionId });
      this.onEvent?.('recognition_start', { provider: detail?.provider || 'android_speech_recognizer' });
      return;
    }
    if (state === 'speech_start') {
      recordVoiceDiagnostic(ATHENA_STT_EVENTS.SPEECH_DETECTED, { provider: detail?.provider, sessionId: this.sessionId });
      this.onEvent?.('speech_start', {});
      return;
    }
    if (state === 'speech_end') {
      recordVoiceDiagnostic(ATHENA_STT_EVENTS.SPEECH_ENDED, { provider: detail?.provider, sessionId: this.sessionId });
      this.onEvent?.('speech_end', {});
      this._emitRecognitionLevel(0, detail?.provider);
      return;
    }
    if (state === 'ended') this._end();
  }

  _onPartial(detail) {
    if (!this._matches(detail) || typeof detail?.transcript !== 'string') return;
    recordVoiceDiagnostic(ATHENA_STT_EVENTS.PARTIAL_TRANSCRIPT, { provider: detail?.provider, sessionId: this.sessionId });
    const resultIndex = Number(detail?.resultIndex) || 0;
    const segmentId = this._pendingSegmentId
      || `native-${resultIndex}-${++this._segmentCounter}`;
    this._pendingSegmentId = segmentId;
    this.onResult?.(detail.transcript, false, { resultIndex, segmentId });
  }

  _onFinal(detail) {
    if (!this._matches(detail) || typeof detail?.transcript !== 'string') return;
    recordVoiceDiagnostic(ATHENA_STT_EVENTS.FINAL_TRANSCRIPT, { provider: detail?.provider, sessionId: this.sessionId });
    const resultIndex = Number(detail?.resultIndex) || 0;
    const segmentId = this._pendingSegmentId
      || `native-${resultIndex}-${++this._segmentCounter}`;
    this._pendingSegmentId = null;
    this.onResult?.(detail.transcript, true, { resultIndex, segmentId });
  }

  _onRecognitionLevel(detail) {
    if (!this._matches(detail)) return;
    const rawLevel = Number(detail?.level);
    if (!Number.isFinite(rawLevel)) return;
    // The native event is already throttled and contains only a normalized
    // numeric level — never audio frames or a recording.
    this._emitRecognitionLevel(rawLevel, detail?.provider);
  }

  _emitRecognitionLevel(level, provider) {
    this.onEvent?.('recognition_level', {
      level: Math.max(0, Math.min(1, Number(level) || 0)),
      provider: provider || 'android_speech_recognizer',
    });
  }

  _onError(detail) {
    if (!this._matches(detail)) return;
    this._emitRecognitionLevel(0, detail?.provider);
    // Se a sessão acabou sem um final, o próximo trecho não pode sobrescrever
    // o parcial anterior no TurnBuffer.
    this._pendingSegmentId = null;
    // Android error 9 (ERROR_INSUFFICIENT_PERMISSIONS) is also emitted when a
    // vendor recognition service cannot access audio. Only the explicit
    // app-level denial emitted by AthenaVoicePlugin becomes `not-allowed`.
    const appPermissionDenied = detail?.reason === 'app_microphone_denied'
      || (detail?.code === 'permission_denied'
        && ['permission_denied', 'permission_permanently_denied'].includes(detail?.reason));
    const reason = appPermissionDenied
      ? 'not-allowed'
      : (detail?.reason || detail?.code || 'recognition_error');
    const recoverable = detail?.recoverable !== false;
    recordVoiceDiagnostic(ATHENA_STT_EVENTS.ERROR, {
      provider: detail?.provider,
      sessionId: this.sessionId,
      errorCode: detail?.code || reason,
    });
    this.onError?.({ error: reason, recoverable });
    if (!recoverable) this._end();
  }

  _fail(reason) {
    if (!this._active) return;
    recordVoiceDiagnostic(ATHENA_STT_EVENTS.ERROR, { provider: 'android_speech_recognizer', sessionId: this.sessionId, errorCode: reason });
    this.onError?.({ error: reason, recoverable: false });
    this._end();
  }

  _end() {
    if (this._ended) return;
    this._emitRecognitionLevel(0);
    this._ended = true;
    this._active = false;
    this._nativeStarted = false;
    if (this._stopFallback) window.clearTimeout(this._stopFallback);
    this._stopFallback = null;
    this._removeListeners();
    recordVoiceDiagnostic(ATHENA_STT_EVENTS.LISTENING_END, { provider: 'android_speech_recognizer', sessionId: this.sessionId });
    this.onEnd?.();
  }

  stop() {
    if (!this._active || this._stopping) return;
    this._stopping = true;
    const plugin = getNativeVoicePlugin();
    this._emitRecognitionLevel(0);
    // `stop` is the explicit second press/send action in manual mode. Native
    // playback receives this flag; cancel/restart/lifecycle cleanup do not.
    if (!this._nativeStarted) {
      // A permissão ou o bridge ainda estavam pendentes. O callback assíncrono
      // em `_connectAndStart` observa `_stopping` e não cria uma nova sessão.
      void plugin?.cancelListening?.({ sessionId: this.sessionId });
      this._end();
      return;
    }
    void plugin?.stopListening?.({
      sessionId: this.sessionId,
      playEndCue: !this.continuous,
    });
    // A proteção não envia texto: só libera o engine se um fabricante não
    // devolver onResults/onError depois de stopListening().
    this._stopFallback = window.setTimeout(() => this._end(), STOP_FALLBACK_MS);
  }

  cancel() {
    if (!this._active) return;
    this._stopping = true;
    const plugin = getNativeVoicePlugin();
    this._emitRecognitionLevel(0);
    // Cancellation is used for lifecycle cleanup and interruption. It is
    // deliberately silent; the native cue policy is keyed by `manualTurnId`
    // and the only confirmation tone is emitted by `stop()` on the second
    // user press.
    void plugin?.cancelListening?.({ sessionId: this.sessionId });
    this._end();
  }

  destroy() {
    this.cancel();
  }

  _removeListeners() {
    const cleanup = this._cleanup.splice(0);
    cleanup.forEach((remove) => {
      try { remove?.(); } catch { /* noop */ }
    });
  }
}

export default NativeSpeechAdapter;
